import type React from 'react';
import { Palette, Hammer, Dumbbell, Compass, Puzzle, Library, PartyPopper } from 'lucide-react';

/**
 * The 7 archetype Avocational **Paths** (AVOCATIONAL_DOMAIN_SPEC §3).
 *
 * A Path is a passion/leisure "class" that organizes Hobby Roles. The 7 Paths
 * are now the domain's navigable AREAS / radar axes (like Intellectual's Knowledge
 * Paths and Existential's Pillars) — see `utils/avocationalPathStats.ts` for the
 * path-as-area rank. (The legacy leisure modes were retired in AV8, tables dropped
 * 2026-06-22; the old derived-parameter radar was retired 2026-07-12.)
 *
 * Each Path carries a crest (icon + color), a one-line meaning, a curated catalog
 * of example hobby roles (+ a custom escape hatch, wired in AV2), and the
 * path-name stored in the DB (`avocational_hobbies.primary_path`).
 */
export type AvocationalPathName =
  | 'Artist'
  | 'Maker'
  | 'Athlete'
  | 'Explorer'
  | 'Strategist'
  | 'Collector'
  | 'Host';

export interface AvocationalPath {
  /** Stored verbatim in the DB (`avocational_hobbies.primary_path`). */
  name: AvocationalPathName;
  /** One-line meaning of the class. */
  meaning: string;
  description: string;
  /** Crest icon (Lucide component). */
  icon: React.ElementType;
  /** Crest color (also the path accent). */
  color: string;
  /** Curated hobby-role catalog — the picker options for AV2 (+ custom). */
  exampleRoles: string[];
}

export const AVOCATIONAL_PATHS: Record<AvocationalPathName, AvocationalPath> = {
  Artist: {
    name: 'Artist',
    meaning: 'Creative self-expression',
    description:
      'Make and express — art, music, writing, performance. The Artist gives feeling and ideas a form, for the joy of expression itself.',
    icon: Palette,
    color: '#EC4899',
    exampleRoles: ['Painter', 'Musician', 'Writer', 'Photographer', 'Dancer', 'Filmmaker', 'Poet', 'Digital Artist'],
  },
  Maker: {
    name: 'Maker',
    meaning: 'Hands-on crafting & building',
    description:
      'Build with your hands — craft, grow, cook, repair. The Maker delights in shaping real things and the calm focus of the craft.',
    icon: Hammer,
    color: '#F59E0B',
    exampleRoles: ['Gardener', 'Cook', 'Woodworker', 'Potter', 'Tailor', 'Jewelry Maker', 'DIY Builder', 'Home Stylist'],
  },
  Athlete: {
    name: 'Athlete',
    meaning: 'Recreational physical mastery',
    description:
      'Play with your body — sport, movement, physical challenge. The Athlete chases the flow and aliveness of moving well, for fun.',
    icon: Dumbbell,
    color: '#EF4444',
    exampleRoles: ['Runner', 'Footballer', 'Martial Artist', 'Swimmer', 'Cyclist', 'Climber', 'Dancer', 'Horse Rider'],
  },
  Explorer: {
    name: 'Explorer',
    meaning: 'Adventure, travel & discovery',
    description:
      'Seek the new — travel, nature, novel experiences. The Explorer follows curiosity outward and collects experiences over things.',
    icon: Compass,
    color: '#10B981',
    exampleRoles: ['Traveler', 'Hiker', 'Camper', 'Diver', 'Urban Explorer', 'Cultural Explorer', 'Nature Walker'],
  },
  Strategist: {
    name: 'Strategist',
    meaning: 'Games, puzzles & mental challenge',
    description:
      'Play with your mind — games, puzzles, systems. The Strategist loves the satisfying click of a problem solved and a game well played.',
    icon: Puzzle,
    color: '#3B82F6',
    exampleRoles: ['Chess Player', 'Gamer', 'Board Gamer', 'Puzzle Solver', 'Card Player', 'Fantasy-Sports Player'],
  },
  Collector: {
    name: 'Collector',
    meaning: 'Appreciation, curation & collecting',
    description:
      'Gather and curate — collect, organize, appreciate. The Collector finds joy in the hunt, the set, and deep appreciation of a domain.',
    icon: Library,
    color: '#A855F7',
    exampleRoles: ['Book Collector', 'Watch Collector', 'Art Collector', 'Car Enthusiast', 'Sneaker Collector', 'Coin Collector', 'Antique Collector'],
  },
  Host: {
    name: 'Host',
    meaning: 'Social recreation & shared experience',
    description:
      'Bring people together — gatherings, events, shared fun. The Host creates the occasions where others connect and play.',
    icon: PartyPopper,
    color: '#14B8A6',
    exampleRoles: ['Event Host', 'Community Organizer', 'Dinner Host', 'Game-Night Host', 'Majlis Host', 'Experience Curator'],
  },
};

export const AVOCATIONAL_PATH_NAMES: AvocationalPathName[] = [
  'Artist',
  'Maker',
  'Athlete',
  'Explorer',
  'Strategist',
  'Collector',
  'Host',
];

/**
 * Normalize a stored path value to an archetype Path. Defaults anything unknown
 * or missing to `Artist`, so the UI never reads an undefined crest config. (Every
 * hobby has carried `primary_path` since the AV0 migration backfilled it.)
 */
export function resolveAvocationalPath(value: string | null | undefined): AvocationalPathName {
  if (value && value in AVOCATIONAL_PATHS) return value as AvocationalPathName;
  return 'Artist';
}
